import React from 'react'
import { Can } from 'config/ability'
import { renderDate } from 'utils/renderDate'
import { Item } from './DetailsList'
import { type ApiApplicationLogs } from 'types/Application'
import useQueryGet from 'hooks/useQueryGet'
import { useTranslation } from 'react-i18next'
import { Box, Grid, Link } from '@mui/material'
import RequestStatus from 'components/RequestStatus'
import { StatusTypes } from '../applicationStatuses'
import { DetailsRow } from './features/details-row'

export const History = ({ applicationId, marketCode }: { marketCode: string; applicationId?: string }): JSX.Element => {
  const { t } = useTranslation()
  const { data, isLoading } = useQueryGet<ApiApplicationLogs>({
    queryKey: ['application-detail-logs', applicationId],
    endpoint: `applications/logs/${applicationId ?? ''}`,
  })

  if (data === null || data === undefined || 'error' in data) {
    return <RequestStatus data={data} isLoading={isLoading} />
  }

  return (
    <>
      {!!data.logs?.length && (
        <DetailsRow
          title={t('application.applicationHistory')}
          colsOnDesktop={1}
          description="List of mailings sent to the consumer"
        >
          {data.logs?.map((item, idx) => (
            <React.Fragment key={`application-log-${item.log.createdAt}`}>
              <Grid
                container
                sx={{ borderBottom: idx !== data.logs.length - 1 ? '1px solid' : '', borderColor: 'grey.200' }}
              >
                <Grid sx={{ paddingTop: '0 !important' }} item xs={12} lg={6}>
                  <Item label={t('application.date')} text={renderDate(item.log.createdAt, true)} />
                </Grid>
                <Grid sx={{ paddingTop: '0 !important' }} item xs={12} lg={6}>
                  <Item label={t('application.description')} text={StatusTypes[+item.log.description] || 'no data'} />
                </Grid>
              </Grid>
            </React.Fragment>
          ))}
        </DetailsRow>
      )}
      <br />
      <br />
      {!!data?.mailLogs?.length && (
        <DetailsRow
          title={t('application.applicationMailHistory')}
          description="List of mailings sent to the consumer"
          colsOnDesktop={1}
        >
          {data?.mailLogs?.map((item, idx) => (
            <React.Fragment key={`application-log-${item.mailLog.id}`}>
              <Grid
                container
                sx={{ borderBottom: idx !== data.logs.length - 1 ? '1px solid' : '', borderColor: 'grey.200' }}
              >
                <Grid sx={{ paddingTop: '0 !important' }} item xs={12} lg={4}>
                  <Item
                    label={t('application.template')}
                    text={
                      <>
                        {item.mailLog.template} ({item.mailLog.status})
                      </>
                    }
                  />
                </Grid>
                <Grid sx={{ paddingTop: '0 !important' }} item xs={12} lg={4}>
                  <Item label={t('application.date')} text={renderDate(item.mailLog.createdAt, true)} />
                </Grid>
                <Grid sx={{ paddingTop: '0 !important' }} item xs={12} lg={4}>
                  <Can I="read" a="MailTemplate">
                    <Item
                      label={t('application.actions')}
                      text={
                        <Box display="flex" gap={2}>
                          {item.mailLogHash !== undefined && item.applicationHash !== '' ? (
                            <Link
                              target="_blank"
                              href={`${process.env.REACT_APP_WEB_URL}/${marketCode}/email?hash=${item.applicationHash}&template=${item.mailLog.template}`}
                            >
                              {t('common.template')}
                            </Link>
                          ) : null}
                          {item.mailLogHash !== undefined && item.mailLogHash !== '' ? (
                            <Link
                              target="_blank"
                              href={`${process.env.REACT_APP_WEB_URL}/${marketCode}/email?hash=${item.mailLogHash}&template=${item.mailLog.template}&log=true`}
                            >
                              {t('common.preview')}
                            </Link>
                          ) : null}
                        </Box>
                      }
                    />
                  </Can>
                </Grid>
              </Grid>
            </React.Fragment>
          ))}
        </DetailsRow>
      )}
      <br />
      <br />
      {!!data?.prizes?.length && (
        <DetailsRow
          title={t('application.prizes')}
          description="List of prizes assigned to the application"
          colsOnDesktop={1}
        >
          {data?.prizes?.map((item, idx) => (
            <React.Fragment key={`application-prize-${idx}`}>
              <Grid
                container
                sx={{ borderBottom: idx !== data.logs.length - 1 ? '1px solid' : '', borderColor: 'grey.200' }}
              >
                <Grid sx={{ paddingTop: '0 !important' }} item xs={12} lg={6}>
                  <Item
                    label={t('application.prizeName')}
                    text={
                      <>
                        {item.prizeGroup.name} ({item.prizeGroup.type})
                      </>
                    }
                  />
                </Grid>
                <Grid sx={{ paddingTop: '0 !important' }} item xs={12} lg={6}>
                  <Item label={t('application.status')} text={item.status} />
                </Grid>
              </Grid>
            </React.Fragment>
          ))}
        </DetailsRow>
      )}
    </>
  )
}
